import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { IUser } from '@epicuro-next/data/users';
import { LegacyAccount } from '@epicuro-next/models/accounts';
import {
  Account,
  AccountCountry,
  AccountReviewer,
  AccountsQuery,
  Site,
} from 'graphql-api/gql-api-interfaces';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import {
  AccountCountriesGQL,
  AccountDetailsGQL,
  AccountDetailsQuery,
  AccountDetailsQueryVariables,
  AccountGQL,
  AccountReviewersGQL,
} from '../queries';
import { ACCOUNTS_API_TOKEN, SITES_API_TOKEN } from '../tokens';
@Injectable()
export class AccountsService {
  constructor(
    private accountsGQL: AccountGQL,
    private accountDetailsGQL: AccountDetailsGQL,
    private accountCountriesGQL: AccountCountriesGQL,
    private accountReviewersGQL: AccountReviewersGQL,
    @Inject(SITES_API_TOKEN) private siteUrl: string,
    private http: HttpClient,
    @Inject(ACCOUNTS_API_TOKEN) private readonly accountsUrl: string,
  ) {}

  public getAccounts(
    query: AccountsQuery,
  ): Observable<{ list: Account[]; total: number }> {
    return this.accountsGQL
      .fetch({
        query,
      })
      .pipe(
        take(1),
        map((queryResult) => {
          return {
            list: queryResult.data.accounts?.items as Account[],
            total: queryResult.data.accounts?.total as number,
          };
        }),
        catchError(() => throwError(() => new Error('Failed to get accounts'))),
      );
  }

  public getAccountCountries(accountId: string): Observable<AccountCountry[]> {
    return this.accountCountriesGQL.fetch({ accountId }).pipe(
      take(1),
      map((queryResult) => {
        return queryResult.data.accountCountries;
      }),
      catchError(() =>
        throwError(() => new Error('Failed to get account countries')),
      ),
    );
  }

  public getAccountDetails(
    query: AccountDetailsQueryVariables,
  ): Observable<Account> {
    return this.accountDetailsGQL.fetch(query).pipe(
      take(1),
      map((queryResult: ApolloQueryResult<AccountDetailsQuery>) => {
        return queryResult.data.account;
      }),
      catchError(() => throwError(() => new Error('Failed to get account'))),
    );
  }

  public getSites(): Observable<Site[]> {
    return this.http.get<Site[]>(this.siteUrl);
  }

  public getAccountReviewers(accountId: string): Observable<AccountReviewer[]> {
    return this.accountReviewersGQL.fetch({ accountId }).pipe(
      take(1),
      map((queryResult) => {
        return queryResult.data.accountReviewers ?? [];
      }),
      catchError(() =>
        throwError(() => new Error('Failed to get account reviewers')),
      ),
    );
  }

  public getSalesRepresentatives(accountIds: string[]): Observable<IUser[]> {
    // When the API supports multiple accountsIds at once, this map + forkJoin is no longer needed.
    const requests$ = accountIds.map((accountId) =>
      this.http.get<IUser[]>(
        `${this.accountsUrl}/${accountId}/users/salesrepresentatives`,
      ),
    );

    return forkJoin(requests$).pipe(
      map((response) => {
        const seed: IUser[] = [];
        return response.reduce((acc, val) => acc.concat(val), seed);
      }),
    );
  }

  public getUserAssociatedAccounts() {
    return this.http.get<LegacyAccount[]>(`${this.accountsUrl}`);
  }

  public downloadVmiDDrReport(accountId: string, brandId: string) {
    return this.http.get(
      `${this.accountsUrl}/reports/mdm/${accountId}/${brandId}`,
      {
        responseType: 'blob',
        observe: 'response',
      },
    );
  }
}
